<template>
  <FeatureFlagged v-if="approvalWarning && approvalState" name="approval">
    <WdsAlert
      class="tw-mb-4"
      icon
      :error="approvalState.state === ApprovalApprovableState.Rejected"
      :warning="approvalState.state !== ApprovalApprovableState.Rejected"
      :title="approvalWarning"
    />
  </FeatureFlagged>
</template>

<script setup lang="ts">
import { WdsAlert } from '@worksome/wds-components'
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { ApprovalApprovableState, ApprovalTrigger } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { graphql, useFragment } from '@/gql'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

const props = defineProps<{
  hireId: string
}>()

const HireFragment = graphql(`
  fragment HireAlertFields on Hire {
    activeContract {
      id
    }
    hireChangedState: currentApprovalState(trigger: HIRE_CHANGED) {
      approvalApprovable {
        approval {
          trigger
        }
      }
      actionedBy {
        name
      }
      createdAt
      state
    }
    hireCreatedState: currentApprovalState(trigger: HIRE_CREATED) {
      approvalApprovable {
        approval {
          trigger
        }
      }
      actionedBy {
        name
      }
      createdAt
      state
    }
  }
`)

const GET_HIRE = graphql(`
  query GetHireForHeaderAlerts($id: ID!) {
    hire(id: $id) {
      ...HireAlertFields
    }
  }
`)

const { result, loading } = useQuery(GET_HIRE, () => ({ id: props.hireId }), {
  clientId: 'publicApi',
})

const hire = computed(() => useFragment(HireFragment, result.value?.hire))

const approvalState = computed(() =>
  hire.value?.activeContract?.id ? hire.value?.hireChangedState : hire.value?.hireCreatedState,
)

const approvalWarning = computed(() => {
  const overallApprovalState = approvalState.value

  if (
    !overallApprovalState ||
    overallApprovalState.state === ApprovalApprovableState.Approved ||
    (overallApprovalState.approvalApprovable.approval.trigger === ApprovalTrigger.HireChanged &&
      overallApprovalState.state === ApprovalApprovableState.Rejected)
  ) {
    return null
  }

  switch (overallApprovalState.state) {
    case ApprovalApprovableState.Requested:
      return $t('Internal approval needed')
    case ApprovalApprovableState.Rejected:
      return $t('Approval denied by :user on :date', {
        user: overallApprovalState.actionedBy.name,
        date: overallApprovalState.createdAt,
      })
    default:
      return null
  }
})
</script>
